import { useEffect } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { useWorkspace } from '@/features/workspace/context';
import { errorMessage, request, requestVoid } from '@/shared/api/client';
import { t } from '@/shared/i18n';
import { toast } from '@/shared/ui/toastStore';
import { useOnline } from '@/shared/offline/network';

/** Фирменная палитра Пассворка, по порядку в выборе цвета (как CalendarEvent::COLORS на сервере). */
export const EVENT_COLORS = ['red', 'orange', 'yellow', 'green', 'teal', 'sky', 'blue', 'purple', 'pink', 'beige', 'silver', 'slate'] as const;
export type EventColor = (typeof EVENT_COLORS)[number];
export const RESPONSES = ['yes', 'maybe', 'no'] as const;
export type Response = (typeof RESPONSES)[number];
const responseSchema = z.enum(['pending', 'yes', 'maybe', 'no']);
export type AttendeeResponse = z.infer<typeof responseSchema>;

/** Вид события, как в Notion Calendar: событие, фокус-время, «нет на месте», день рождения (свой тип, не Google). */
/** Виды событий; «Не беспокоить» (dnd) ставит сам Chat вместе со статусом — вручную его не выбирают. */
export const EVENT_TYPES = ['event', 'focus', 'ooo', 'birthday', 'dnd'] as const;
export type EventType = (typeof EVENT_TYPES)[number];
export const PICKABLE_TYPES = EVENT_TYPES.filter((x) => x !== 'dnd');
export type Visibility = 'public' | 'private';

/** Прикреплённое к событию: ссылка на сайт (заголовок из превью) или сообщение мессенджера (карточкой). */
const messageCardSchema = z.object({ authorId: z.string().nullable(), text: z.string(), createdAt: z.string(), threadRootId: z.string().nullable().default(null) });
export const linkSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('url'), url: z.string(), title: z.string().nullable().default(null), siteName: z.string().nullable().default(null) }),
  z.object({ kind: z.literal('message'), messageId: z.string(), conversationId: z.string().nullable().default(null), message: messageCardSchema.nullable().default(null) }),
]);
export type EventLink = z.infer<typeof linkSchema>;
export type MessageCard = z.infer<typeof messageCardSchema>;

export const eventSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  start: z.string(),
  end: z.string(),
  allDay: z.boolean(),
  /** Пояс, в котором назначили встречу (IANA); старые ответы без него — null. */
  timeZone: z.string().nullable().optional(),
  color: z.enum(EVENT_COLORS).nullable().catch(null),
  conversationId: z.string().nullable(),
  createdById: z.string(),
  // Участник — человек пространства или внешний гость по почте (у него нет userId).
  attendees: z.array(z.union([z.object({ userId: z.string(), response: responseSchema }), z.object({ email: z.string(), response: responseSchema })])),
  myResponse: responseSchema.nullable(),
  editable: z.boolean(),
  type: z.enum(EVENT_TYPES).catch('event').default('event'),
  /** Без окончания («Не беспокоить, пока не выключу»): конец — край дня, на сетке растворяется. */
  openEnded: z.boolean().optional(),
  visibility: z.enum(['public', 'private']).catch('public').default('public'),
  call: z.boolean().default(false),
  /** Свой календарь (id), календарь Google («google:аккаунт:календарь») или null — календарь по умолчанию. */
  calendarId: z.string().nullable().default(null),
  googleSynced: z.boolean().default(false),
  links: z.array(linkSchema).default([]),
  /** Права гостей, как в Google Календаре. */
  permissions: z.object({ modify: z.boolean(), invite: z.boolean(), seeList: z.boolean(), moveTime: z.boolean().default(true) }).default({ modify: false, invite: true, seeList: true, moveTime: true }),
  /** Гость может перенести — это станет предложением организатору. */
  canPropose: z.boolean().default(false),
  /** Участник переносит событие сам («все участники могут менять время»). */
  canMove: z.boolean().default(false),
  /** Предложенный перенос, пока организатор не ответил: призрачный блок на сетке. */
  pendingMove: z.object({ id: z.string(), start: z.string(), end: z.string(), byId: z.string() }).nullable().default(null),
  /** Ответ на PATCH гостя: время ушло организатору предложением. */
  proposed: z.boolean().optional(),
});
export type CalendarEvent = z.infer<typeof eventSchema>;

// Встреча из подключённого календаря. На весь день — start/end датами «ГГГГ-ММ-ДД» (конец не входит); busy: false —
// «свободное время», рисуется бледнее.
const externalSchema = z.object({ uid: z.string(), title: z.string(), start: z.string(), end: z.string(), url: z.string().nullable(), allDay: z.boolean().default(false), busy: z.boolean().default(true) });
export type ExternalEvent = z.infer<typeof externalSchema>;
const todoSchema = z.object({ id: z.string(), title: z.string(), remindAt: z.string(), done: z.boolean(), messageId: z.string().nullable(), conversationId: z.string().nullable(), ownTitle: z.string().nullable().default(null), messageText: z.string().nullable().default(null), messageAuthorId: z.string().nullable().default(null) });
export type CalendarTodo = z.infer<typeof todoSchema>;

// Событие из подключённого Google Календаря (через API): только чтение.
const googleEventSchema = z.object({
  uid: z.string(),
  title: z.string(),
  start: z.string(),
  end: z.string(),
  url: z.string().nullable(),
  allDay: z.boolean(),
  busy: z.boolean(),
  type: z.enum(EVENT_TYPES).catch('event'),
  private: z.boolean().default(false),
  calendarId: z.string(),
  /** Пояс встречи в Google (IANA), если он указан. */
  timeZone: z.string().nullable().optional(),
  /** Организатор и гости из Google; почта сопоставляется с участниками пространства. */
  organizer: z.object({ email: z.string(), name: z.string().nullable(), self: z.boolean() }).nullable().optional(),
  attendees: z.array(z.object({ email: z.string(), name: z.string().nullable(), response: z.enum(['yes', 'maybe', 'no', 'pending']).catch('pending'), self: z.boolean() })).optional(),
});
export type GoogleEvent = z.infer<typeof googleEventSchema>;

// Мой отпуск из базы знаний «Отпуска»: весь день, только чтение — правят в базе. Даты — с поясом человека.
const vacationSchema = z.object({ key: z.string(), rowId: z.string(), start: z.string(), end: z.string(), substitutes: z.array(z.string()).default([]) });

const calendarSchema = z.object({ events: z.array(eventSchema), external: z.array(externalSchema), google: z.array(googleEventSchema).default([]), vacations: z.array(vacationSchema).optional(), todos: z.array(todoSchema) });
export type CalendarData = z.infer<typeof calendarSchema>;

export const calendarKey = (wid: string) => ['ws', wid, 'calendar'] as const;

/**
 * Календарь хранится помесячно, как в Notion Calendar: месяц — один запрос и одна запись в копии на устройстве,
 * любой вид (день, неделя, месяц, список) собирается из них. Открыл вчера — сегодня события уже на месте,
 * свежие приходят следом.
 */
const MONTH_KEEP_MS = 7 * 24 * 3600_000;
const monthStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const monthId = (d: Date) => `month:${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}`;

/** Месяцы, которые задевает промежуток [from, to). */
export function monthsOf(from: Date, to: Date): Date[] {
  const out: Date[] = [];
  for (let m = monthStart(from); m < to; m = new Date(m.getFullYear(), m.getMonth() + 1, 1)) out.push(m);
  return out;
}

const monthQuery = (wid: string, api: string, month: Date) => {
  const next = new Date(month.getFullYear(), month.getMonth() + 1, 1);
  const range = `from=${encodeURIComponent(month.toISOString())}&to=${encodeURIComponent(next.toISOString())}`;
  return {
    queryKey: [...calendarKey(wid), monthId(month)],
    queryFn: () => request('GET', `${api}/calendar?${range}`, calendarSchema),
    staleTime: 30_000,
    gcTime: MONTH_KEEP_MS,
  };
};

/**
 * Календарь всегда доступен без сети: прошлый месяц, текущий и три вперёд грузятся заранее и лежат в копии
 * на устройстве (snapshot.ts). К открытию календаря всё уже в памяти.
 */
export const OFFLINE_MONTHS = [0, 1, -1, 2, 3] as const;
export function prefetchCalendar(client: QueryClient, wid: string, api: string, now: Date = new Date()): void {
  for (const shift of OFFLINE_MONTHS) client.query(monthQuery(wid, api, new Date(now.getFullYear(), now.getMonth() + shift, 1))).catch(() => undefined);
}

const inRange = (start: string, end: string, from: number, to: number) => Date.parse(end) > from && Date.parse(start) < to;
const uniq = <T,>(items: readonly T[], keyOf: (x: T) => string): T[] => [...new Map(items.map((x) => [keyOf(x), x])).values()];

/** Склеить месяцы в один ответ для промежутка: событие на стыке месяцев — один раз. */
function sliceOf(parts: readonly CalendarData[], from: Date, to: Date): CalendarData {
  const a = from.getTime();
  const b = to.getTime();
  const on = (start: string, end: string) => inRange(start, end, a, b);
  return {
    events: uniq(parts.flatMap((p) => p.events), (e) => `${e.id}|${e.start}`).filter((e) => on(e.start, e.end)),
    external: uniq(parts.flatMap((p) => p.external), (e) => `${e.uid}|${e.start}`).filter((e) => on(e.start, e.end)),
    google: uniq(parts.flatMap((p) => p.google), (e) => `${e.calendarId}|${e.uid}|${e.start}`).filter((e) => on(e.start, e.end)),
    vacations: uniq(parts.flatMap((p) => p.vacations ?? []), (v) => v.key).filter((v) => on(v.start, v.end)),
    todos: uniq(parts.flatMap((p) => p.todos), (x) => x.id).filter((x) => Date.parse(x.remindAt) >= a && Date.parse(x.remindAt) < b),
  };
}

export function useCalendar(from: Date, to: Date) {
  const { workspace, api } = useWorkspace();
  const client = useQueryClient();
  // Соседние месяцы — заранее: листаешь через границу месяца, а события уже в памяти, пустой сетки нет.
  const lo = from.getTime();
  const hi = to.getTime();
  useEffect(() => {
    for (const m of [...monthsOf(new Date(lo - 7 * 86_400_000), new Date(hi + 7 * 86_400_000))]) client.query(monthQuery(workspace.id, api, m)).catch(() => undefined);
  }, [client, workspace.id, api, lo, hi]);
  const results = useQueries({ queries: monthsOf(from, to).map((m) => monthQuery(workspace.id, api, m)) });
  // Пока месяц грузится, показываем то, что уже есть (из памяти или с устройства), а не пустую сетку.
  const parts = results.flatMap((r) => (r.data === undefined ? [] : [r.data]));
  const data = parts.length > 0 ? sliceOf(parts, from, to) : undefined;
  const failed = results.find((r) => r.isError);
  const online = useOnline();
  return {
    data,
    /** Нет сети, а часть этих дней не сохранена на устройстве: сказать, а не показывать пустую сетку как «встреч нет». */
    offlineGap: !online && results.some((r) => r.data === undefined),
    isPending: data === undefined && results.some((r) => r.isPending),
    isFetching: results.some((r) => r.isFetching),
    isError: data === undefined && failed !== undefined,
    error: failed?.error ?? null,
  };
}

export interface EventInput {
  readonly title: string;
  readonly description: string;
  readonly start: string;
  readonly end: string;
  readonly allDay: boolean;
  /** Пояс встречи: время в редакторе введено в нём. */
  readonly timeZone?: string | null;
  readonly color: EventColor | null;
  readonly conversationId: string | null;
  readonly attendeeIds: readonly string[];
  /** Внешние гости — по почте. */
  readonly attendeeEmails: readonly string[];
  readonly type?: EventType;
  readonly visibility?: Visibility;
  readonly call?: boolean;
  readonly calendarId?: string | null;
  readonly links?: readonly LinkInput[];
  readonly guestsCanModify?: boolean;
  readonly guestsCanMoveTime?: boolean;
  readonly guestsCanInvite?: boolean;
  readonly guestsCanSeeList?: boolean;
}

export type LinkInput = { readonly kind: 'url'; readonly url: string; readonly title: string | null; readonly siteName: string | null } | { readonly kind: 'message'; readonly messageId: string };

/** Ссылка события — в вид для отправки на сервер. */
export function linkInput(l: EventLink): LinkInput {
  return l.kind === 'url' ? { kind: 'url', url: l.url, title: l.title, siteName: l.siteName } : { kind: 'message', messageId: l.messageId };
}

/** Короткое название встречи по сообщению, из которого её создают (нейросеть компании). Пусто — темы не видно. */
export function useSuggestEventTitle() {
  const { api } = useWorkspace();
  return useMutation({ mutationFn: (messageId: string) => request('POST', `${api}/calendar/title-suggestion`, z.object({ title: z.string() }), { body: { messageId } }).then((r) => r.title) });
}

// ——— Календари слева: свои, Google, iCal ———
const ownCalendarSchema = z.object({ id: z.string(), name: z.string().nullable(), color: z.enum(EVENT_COLORS).catch('blue'), visibility: z.enum(['public', 'private']).catch('public'), isDefault: z.boolean() });
export type OwnCalendar = z.infer<typeof ownCalendarSchema>;
const googleCalendarSchema = z.object({ id: z.string(), googleId: z.string(), name: z.string(), color: z.enum(EVENT_COLORS).catch('blue'), writable: z.boolean(), primary: z.boolean() });
const googleAccountSchema = z.object({ id: z.string(), email: z.string(), error: z.string().nullable(), calendars: z.array(googleCalendarSchema) });
const calendarsSchema = z.object({ calendars: z.array(ownCalendarSchema), google: z.array(googleAccountSchema), googleEnabled: z.boolean(), ical: z.object({ error: z.string().nullable() }).nullable() });
export type CalendarList = z.infer<typeof calendarsSchema>;

export const calendarsKey = (wid: string) => ['ws', wid, 'calendar', 'calendars'] as const;

export function useCalendars() {
  const { workspace, api } = useWorkspace();
  return useQuery({ queryKey: calendarsKey(workspace.id), queryFn: () => request('GET', `${api}/calendar/calendars`, calendarsSchema), staleTime: 60_000 });
}

/** Календарь по умолчанию — первый (сервер ставит его первым). */
export function defaultCalendar(list: CalendarList | undefined): OwnCalendar | undefined {
  return list?.calendars.find((c) => c.isDefault);
}

export function useCalendarActions() {
  const { workspace, api } = useWorkspace();
  const client = useQueryClient();
  const refresh = () => {
    void client.invalidateQueries({ queryKey: calendarKey(workspace.id) });
  };
  const fail = (e: unknown) => {
    toast(errorMessage(e));
    refresh();
  };
  const own = z.object({ id: z.string(), name: z.string().nullable(), color: z.string(), visibility: z.string(), isDefault: z.boolean() });
  return {
    create: useMutation({ mutationFn: (input: { readonly name: string; readonly color: EventColor }) => request('POST', `${api}/calendar/calendars`, own, { body: input }), onSuccess: refresh, onError: fail }),
    update: useMutation({
      mutationFn: ({ id, ...input }: { readonly id: string; readonly name?: string; readonly color?: EventColor; readonly visibility?: Visibility; readonly isDefault?: true }) => request('PATCH', `${api}/calendar/calendars/${id}`, own, { body: input }),
      onSuccess: refresh,
      onError: fail,
    }),
    remove: useMutation({ mutationFn: (id: string) => requestVoid('DELETE', `${api}/calendar/calendars/${id}`), onSuccess: refresh, onError: fail }),
    disconnect: useMutation({
      mutationFn: (id: string) => requestVoid('DELETE', `${api}/calendar/google/${id}`),
      onSuccess: () => {
        toast(t('Google Календарь отключён'));
        refresh();
      },
      onError: fail,
    }),
    reload: useMutation({ mutationFn: (id: string) => requestVoid('POST', `${api}/calendar/google/${id}/refresh`), onSuccess: refresh, onError: fail }),
  };
}

/** Адрес, по которому браузер уходит к Google за разрешением (ответ — снова «Календарь» с ?google=…). */
export function googleConnectUrl(api: string): string {
  return `${api}/calendar/google/connect`;
}

// ——— Календари коллег и «Подобрать время» ———
const busySchema = z.object({
  key: z.string(),
  eventId: z.string().nullable(),
  title: z.string().nullable(),
  start: z.string(),
  end: z.string(),
  allDay: z.boolean(),
  type: z.enum(EVENT_TYPES).catch('event'),
  openEnded: z.boolean().optional(),
  busy: z.boolean(),
  source: z.string(),
  /** Отпуск: кто заменяет (человек из базы «Отпуска»). */
  substitutes: z.array(z.string()).optional(),
});
export type BusyBlock = z.infer<typeof busySchema>;
/** google: нет подключённого Google Календаря у человека, он есть или отвалился — от этого зависит, полная ли его занятость. */
const peopleSchema = z.object({ people: z.array(z.object({ userId: z.string(), google: z.enum(['none', 'ok', 'error']).optional(), events: z.array(busySchema) })) });
export type PeopleBusy = z.infer<typeof peopleSchema>['people'];

/**
 * Занятость коллег хранится по человеку и месяцу (как свои события): добавил ещё одного коллегу — грузится только он,
 * остальные уже в копии на устройстве. Запросы идут параллельно, а не одним длинным ответом на всех: сервер ходит в
 * Google за каждым отдельно. Копия живёт неделю — открыл вчера, сегодня неделя с коллегами на месте, свежее приходит следом.
 */
const personBusyQuery = (wid: string, api: string, userId: string, from: Date, to: Date) => {
  const range = `from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`;
  return {
    queryKey: [...calendarKey(wid), 'people', userId, range],
    queryFn: async () => {
      const one = (await request('GET', `${api}/calendar/people?${range}&userIds=${userId}`, peopleSchema)).people[0];
      return one ?? { userId, events: [] };
    },
    staleTime: 30_000,
    gcTime: MONTH_KEEP_MS,
  };
};

/** Заранее подгрузить коллег из «Календарей коллег»: текущий месяц первым, соседние следом. */
export function prefetchPeople(client: QueryClient, wid: string, api: string, userIds: readonly string[], now: Date = new Date()): void {
  for (const shift of [0, 1, -1]) {
    const from = new Date(now.getFullYear(), now.getMonth() + shift, 1);
    const to = new Date(now.getFullYear(), now.getMonth() + shift + 1, 1);
    for (const id of userIds) client.query(personBusyQuery(wid, api, id, from, to)).catch(() => undefined);
  }
}

export function usePeople(userIds: readonly string[], from: Date, to: Date): { readonly data: PeopleBusy | undefined } {
  const { workspace, api } = useWorkspace();
  const ids = [...new Set(userIds)].sort();
  const results = useQueries({
    queries: ids.map((id) => ({ ...personBusyQuery(workspace.id, api, id, from, to), placeholderData: (prev: PeopleBusy[number] | undefined) => prev })),
  });
  const loaded = results.flatMap((r) => (r.data === undefined ? [] : [r.data]));
  // Порядок и состав — как в запросе; пока человек грузится, остальные уже на месте.
  return { data: ids.length === 0 || loaded.length === 0 ? undefined : loaded };
}

/** Одно событие — карточка в личке «Календаря» и по ссылке в сообщении. 404 — событие отменили. */
export function useEvent(id: string) {
  const { workspace, api } = useWorkspace();
  return useQuery({ queryKey: [...calendarKey(workspace.id), 'event', id], queryFn: () => request('GET', `${api}/calendar/events/${id}`, eventSchema), retry: false, staleTime: 30_000 });
}

/** Поменять событие во всех загруженных промежутках сразу — перенос и растягивание видны без ожидания сервера. */
/** Только месяцы сетки (…, 'month:2026-10'), а не список календарей и не занятость коллег под тем же ключом. */
const rangeQuery = (wid: string) => ({ queryKey: calendarKey(wid), predicate: (q: { readonly queryKey: readonly unknown[] }) => q.queryKey.length === 4 && typeof q.queryKey[3] === 'string' && q.queryKey[3].startsWith('month:') });

function patchCached(client: QueryClient, wid: string, id: string, patch: (e: CalendarEvent) => CalendarEvent | null): void {
  client.setQueriesData<CalendarData>(rangeQuery(wid), (old) => {
    if (old === undefined) return old;
    const events = old.events.flatMap((e) => {
      if (e.id !== id) return [e];
      const next = patch(e);
      return next === null ? [] : [next];
    });
    return { ...old, events };
  });
}

export function useEventActions() {
  const { workspace, api } = useWorkspace();
  const client = useQueryClient();
  const refresh = () => {
    void client.invalidateQueries({ queryKey: calendarKey(workspace.id) });
  };
  const fail = (e: unknown) => {
    toast(errorMessage(e));
    refresh();
  };
  return {
    create: useMutation({
      mutationFn: (input: EventInput) => request('POST', `${api}/calendar/events`, eventSchema, { body: input }),
      onSuccess: refresh,
      onError: fail,
    }),
    update: useMutation({
      mutationFn: ({ id, ...input }: Partial<EventInput> & { readonly id: string }) => request('PATCH', `${api}/calendar/events/${id}`, eventSchema, { body: input }),
      onMutate: ({ id, ...input }) => {
        // Гость переносит предложением — время у себя не двигаем, ждём призрачный блок с сервера.
        const own = client.getQueriesData<CalendarData>(rangeQuery(workspace.id)).some(([, d]) => d?.events.some((e) => e.id === id && e.editable) === true);
        if (!own) return;
        patchCached(client, workspace.id, id, (e) => ({
          ...e,
          ...(input.title === undefined ? {} : { title: input.title }),
          ...(input.start === undefined ? {} : { start: input.start }),
          ...(input.end === undefined ? {} : { end: input.end }),
          ...(input.allDay === undefined ? {} : { allDay: input.allDay }),
          ...(input.timeZone === undefined ? {} : { timeZone: input.timeZone }),
          ...(input.color === undefined ? {} : { color: input.color }),
        }));
      },
      onSuccess: (e) => {
        if (e.proposed === true) toast(t('Предложили перенести — ждём ответа организатора'));
        refresh();
      },
      onError: fail,
    }),
    remove: useMutation({
      mutationFn: (id: string) => requestVoid('DELETE', `${api}/calendar/events/${id}`),
      onMutate: (id) => {
        patchCached(client, workspace.id, id, () => null);
      },
      onSuccess: () => {
        toast(t('Событие удалено'));
        refresh();
      },
      onError: fail,
    }),
    respond: useMutation({
      mutationFn: ({ id, response }: { readonly id: string; readonly response: Response }) => request('PUT', `${api}/calendar/events/${id}/response`, eventSchema, { body: { response } }),
      onMutate: ({ id, response }) => {
        patchCached(client, workspace.id, id, (e) => ({ ...e, myResponse: response }));
      },
      onSuccess: refresh,
      onError: fail,
    }),
  };
}

// ——— Отпуска из базы знаний ———
const vacationNowSchema = z.object({
  documentId: z.string().nullable(),
  now: z.array(z.object({ userId: z.string(), until: z.string(), substitutes: z.array(z.string()) })),
});

export const vacationsKey = (wid: string) => ['ws', wid, 'vacations'] as const;

/** Есть ли база «Отпуска» и кто в отпуске сейчас (до какого дня и кто заменяет) — статус и подсказка при упоминании. */
export function useVacations() {
  const { workspace, api } = useWorkspace();
  return useQuery({ queryKey: vacationsKey(workspace.id), queryFn: () => request('GET', `${api}/vacations`, vacationNowSchema), staleTime: 60_000 });
}

/** Завести базу «Отпуска» с готовыми свойствами (сотрудник, даты, статус, заменяет). Есть — вернёт её. */
export function useCreateVacationsBase() {
  const { workspace, api } = useWorkspace();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => request('POST', `${api}/vacations`, z.object({ documentId: z.string() })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: vacationsKey(workspace.id) }),
    onError: (e: unknown) => { toast(errorMessage(e)); },
  });
}
